"use client";

import React, { useState } from "react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  EcommerceProductsProvider,
  useEcommerceProducts,
  VariableProductAddToCart,
  ProductPrice,
  EcommerceProduct,
} from "@/integrations/wordpress/WordPressEcommerceProductsProvider";

declare global {
  interface Window {
    wvcClient?: {
      getCurrentWPQueryParams: () => Record<string, any>;
      getCurrentWPQuery: () => any;
    };
  }
}

function ProductDetailConsumer() {
  const { products, loading, error, refetch } = useEcommerceProducts();
  const [activeImage, setActiveImage] = useState<string | null>(null);

  if (loading) {
    return (
      <div className="mx-auto max-w-6xl px-4 py-12 md:py-20 animate-pulse">
        <div className="grid gap-12 lg:grid-cols-2">
          <div className="space-y-4">
            <div className="aspect-square w-full rounded-2xl bg-muted" />
            <div className="flex gap-3">
              {[0, 1, 2].map((_, i) => (
                <div key={i} data-index={i} className="h-20 w-20 rounded-xl bg-muted" />
              ))}
            </div>
          </div>
          <div className="space-y-6">
            <div className="h-10 w-3/4 rounded-md bg-muted" />
            <div className="h-8 w-1/3 rounded-md bg-muted" />
            <div className="space-y-2">
              <div className="h-4 w-full rounded-md bg-muted" />
              <div className="h-4 w-5/6 rounded-md bg-muted" />
            </div>
            <div className="h-12 w-full rounded-md bg-muted" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !products || products.length === 0) {
    return (
      <div className="mx-auto max-w-xl px-4 py-24 text-center">
        <p className="text-lg font-medium text-foreground">
          No pudimos cargar la información del producto.
        </p>
        <button
          type="button"
          onClick={() => refetch()}
          className="mt-4 inline-flex items-center text-sm font-semibold text-primary underline hover:opacity-80"
        >
          Reintentar
        </button>
      </div>
    );
  }

  const product: EcommerceProduct = products[0];
  const images = product.gallery_image_urls && product.gallery_image_urls.length > 0
    ? product.gallery_image_urls
    : product.featured_image_url
      ? [product.featured_image_url]
      : [];
  const mainImage = activeImage ?? images[0] ?? "";

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 md:py-16">
      <div className="grid gap-10 lg:grid-cols-12 lg:gap-14">
        {/* Left Column: Image Gallery */}
        <div className="lg:col-span-6 space-y-4">
          <div className="relative aspect-square overflow-hidden rounded-2xl border border-border bg-card shadow-xs">
            {mainImage ? (
              <img
                src={mainImage}
                alt={product.title}
                className="h-full w-full object-cover object-center"
                loading="lazy"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-muted text-sm text-muted-foreground">
                Sin imagen disponible
              </div>
            )}
          </div>

          {images.length > 1 && (
            <div
              className="flex flex-wrap gap-3"
              role="group"
              aria-label="Imágenes del producto"
            >
              {images.map((url, i) => {
                const isSelected = (activeImage ?? images[0]) === url;
                return (
                  <button
                    key={`${url}-${i}`}
                    data-index={i}
                    type="button"
                    onClick={() => setActiveImage(url)}
                    aria-label={`${product.title} vista ${i + 1}`}
                    aria-current={isSelected ? "true" : undefined}
                    className={`relative h-20 w-20 shrink-0 overflow-hidden rounded-xl border transition-all cursor-pointer ${
                      isSelected
                        ? "border-primary ring-2 ring-primary"
                        : "border-border opacity-70 hover:opacity-100"
                    }`}
                  >
                    <img
                      src={url}
                      alt=""
                      aria-hidden="true"
                      className="h-full w-full object-cover object-center"
                      loading="lazy"
                    />
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Right Column: Title, Price, Descriptions, Add to Cart, Meta */}
        <div className="lg:col-span-6 flex flex-col justify-start space-y-6">
          <header className="space-y-3">
            <h1 className="font-serif text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
              {product.title}
            </h1>
            <ProductPrice
              product={product}
              as="div"
              className="text-2xl font-semibold text-primary"
            />
          </header>

          {product.short_description && (
            <div
              className="text-base text-muted-foreground leading-relaxed [&_p]:mb-2 [&_p:last-child]:mb-0"
              dangerouslySetInnerHTML={{ __html: product.short_description }}
            />
          )}

          <div className="pt-2">
            <VariableProductAddToCart
              product={product}
              text="Agregar al carrito"
              onVariationResolved={(v) => setActiveImage(v?.featured_image_url ?? null)}
              className="w-full"
              buttonClassName="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md font-medium"
            />
          </div>

          {product.long_description && (
            <div className="space-y-3 pt-2">
              <Separator />
              <div className="pt-2">
                <h2 className="text-base font-semibold text-foreground mb-2">
                  Descripción
                </h2>
                <div
                  className="prose text-sm text-muted-foreground leading-relaxed [&_p]:mb-3 [&_p:last-child]:mb-0"
                  dangerouslySetInnerHTML={{ __html: product.long_description }}
                />
              </div>
            </div>
          )}

          {((product.categories && product.categories.length > 0) ||
            (product.tags && product.tags.length > 0)) && (
            <div className="space-y-4 pt-2">
              <Separator />
              <div className="space-y-3 text-sm">
                {product.categories && product.categories.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-foreground">Categorías:</span>
                    <div className="flex flex-wrap gap-1.5">
                      {product.categories.map((cat, i) => (
                        <Badge
                          key={cat.id || i}
                          data-index={i}
                          variant="secondary"
                          className="font-normal"
                        >
                          {cat.link ? (
                            <Link to={cat.link} className="hover:underline">
                              {cat.name}
                            </Link>
                          ) : (
                            cat.name
                          )}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {product.tags && product.tags.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-foreground">Etiquetas:</span>
                    <div className="flex flex-wrap gap-1.5">
                      {product.tags.map((tag, i) => (
                        <Badge
                          key={tag.id || i}
                          data-index={i}
                          variant="outline"
                          className="font-normal"
                        >
                          {tag.link ? (
                            <Link to={tag.link} className="hover:underline">
                              #{tag.name}
                            </Link>
                          ) : (
                            `#${tag.name}`
                          )}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Product() {
  const currentWPQueryParams =
    typeof window !== "undefined" && window.wvcClient?.getCurrentWPQueryParams
      ? window.wvcClient.getCurrentWPQueryParams()
      : {};

  return (
    <section
      id="single-product-template-section"
      data-nav="light"
      className="w-full bg-background text-foreground"
    >
      <EcommerceProductsProvider wp_query={currentWPQueryParams}>
        <ProductDetailConsumer />
      </EcommerceProductsProvider>
    </section>
  );
}